import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, Mail, ArrowUpRight, ShieldCheck, Award } from "lucide-react";
import MadeByWebsiteTech from "@/components/MadeByWebsiteTech";
import TranslateListenBar from "./TranslateListenBar";

// Named exports below (LitLabMark) — kept forwards-compatible with default import.

/**
 * LitLab Layout — self-contained light-blue aerospace theme.
 * Kept independent from the parent WebsiteTech shell so the /litlab/*
 * section can be forked out into its own repo when ready.
 */
const NAV = [
  { to: "/litlab", label: "Home", exact: true },
  { to: "/litlab/about", label: "About" },
  { to: "/litlab/services", label: "Services" },
  { to: "/litlab/industries", label: "Industries" },
  { to: "/litlab/case-studies", label: "Case studies" },
  { to: "/litlab/quote", label: "Quote" },
  { to: "/litlab/contact", label: "Contact" },
];

const CONTACT = {
  phone: "631-643-6792",
  email: "rao@litlab.com",
  address: "97 Marcus Blvd, Hauppauge, NY 11788",
};

export default function LitLabLayout({ children }) {
  return (
    <div className="min-h-screen flex flex-col litlab-root bg-sky-50 text-slate-800">
      <TopBar />
      <NavBar />
      <main className="flex-1" data-testid="litlab-main">{children}</main>
      <TranslateListenBar />
      <Footer />
      <MadeByWebsiteTech variant="light" />
      <style>{`
        .litlab-root { font-family: 'Inter', system-ui, -apple-system, sans-serif; }
        .litlab-serif { font-family: 'Space Grotesk', 'Inter', sans-serif; letter-spacing: -0.01em; }
        .litlab-grid-bg {
          background-image:
            linear-gradient(rgba(14,165,233,0.06) 1px, transparent 1px),
            linear-gradient(90deg, rgba(14,165,233,0.06) 1px, transparent 1px);
          background-size: 44px 44px;
        }
        .litlab-diagonal {
          background: repeating-linear-gradient(-30deg, transparent 0 12px,
            rgba(14,165,233,.06) 12px 13px);
        }
        /* WYSIWYG-rendered article bodies (Services popup, etc.) */
        .litlab-article-body p { margin-bottom: 0.75rem; }
        .litlab-article-body p:last-child { margin-bottom: 0; }
        .litlab-article-body h2 { font-family: 'Space Grotesk','Inter',sans-serif; font-weight: 700; font-size: 1.15rem; margin: 0.75rem 0 0.4rem; color: #0f172a; }
        .litlab-article-body h3 { font-family: 'Space Grotesk','Inter',sans-serif; font-weight: 700; font-size: 1rem; margin: 0.6rem 0 0.3rem; color: #0f172a; }
        .litlab-article-body ul, .litlab-article-body ol { padding-left: 1.25rem; margin: 0.4rem 0 0.75rem; }
        .litlab-article-body ul { list-style: disc; }
        .litlab-article-body ol { list-style: decimal; }
        .litlab-article-body li { margin: 0.15rem 0; }
        .litlab-article-body a { color: #0369a1; text-decoration: underline; }
        .litlab-article-body blockquote { border-left: 3px solid #7dd3fc; padding-left: 0.75rem; color: #475569; font-style: italic; margin: 0.5rem 0; }
        .litlab-article-body strong { font-weight: 700; }
        /* Quill editor sizing in the CMS */
        .litlab-quill-wrap .ql-editor { min-height: 160px; font-size: 0.875rem; background: #fff; }
        .litlab-quill-wrap .ql-toolbar { border-radius: 6px 6px 0 0; }
        .litlab-quill-wrap .ql-container { border-radius: 0 0 6px 6px; }
      `}</style>
    </div>
  );
}

function TopBar() {
  return (
    <div className="hidden md:block bg-sky-900 border-b border-sky-800 text-xs text-sky-100">
      <div className="max-w-6xl mx-auto px-6 flex justify-between items-center h-9">
        <div className="flex items-center gap-4">
          <a href={`tel:${CONTACT.phone}`} className="inline-flex items-center gap-1.5 hover:text-amber-300 transition"><Phone className="w-3 h-3" /> {CONTACT.phone}</a>
          <a href={`mailto:${CONTACT.email}`} className="inline-flex items-center gap-1.5 hover:text-amber-300 transition"><Mail className="w-3 h-3" /> {CONTACT.email}</a>
        </div>
        <div className="flex items-center gap-3">
          <span className="inline-flex items-center gap-1 text-emerald-300"><ShieldCheck className="w-3 h-3" /> Nadcap Accredited</span>
          <span className="inline-flex items-center gap-1 text-emerald-300"><Award className="w-3 h-3" /> AC7006 · ISO/IEC 17025</span>
        </div>
      </div>
    </div>
  );
}

function NavBar() {
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  useEffect(() => { setOpen(false); }, [loc.pathname]);
  return (
    <header className="sticky top-0 z-40 backdrop-blur-md bg-white/85 border-b border-sky-200 shadow-sm">
      <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between" data-testid="litlab-nav">
        <Link to="/litlab" className="flex items-center gap-2.5" data-testid="litlab-logo">
          <LitLabMark className="w-11 h-11" />
          <div className="leading-none">
            <div className="litlab-serif text-lg font-bold text-slate-900">L.I.T. Labs</div>
            <div className="text-[9px] uppercase tracking-[0.2em] text-slate-500 mt-0.5">Metallurgists · Since 1985</div>
          </div>
        </Link>
        <nav className="hidden md:flex items-center gap-1">
          {NAV.map((n) => {
            const active = n.exact ? loc.pathname === n.to : loc.pathname.startsWith(n.to);
            return (
              <Link key={n.to} to={n.to}
                data-testid={`litlab-nav-${n.label.toLowerCase()}`}
                className={`px-3 py-2 text-sm font-semibold rounded transition ${
                  active ? "text-sky-700" : "text-slate-600 hover:text-slate-900"
                }`}>{n.label}</Link>
            );
          })}
          <Link to="/litlab/quote" data-testid="litlab-nav-cta"
            className="ml-3 inline-flex items-center gap-1.5 bg-amber-500 hover:bg-amber-400 text-white px-4 py-2 rounded font-bold text-sm transition shadow-sm">
            Request quote <ArrowUpRight className="w-3.5 h-3.5" />
          </Link>
        </nav>
        <button className="md:hidden text-slate-700" onClick={() => setOpen((s) => !s)}
          data-testid="litlab-nav-mobile-toggle">
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>
      {open && (
        <nav className="md:hidden bg-white border-t border-sky-200 px-6 py-3 space-y-1">
          {NAV.map((n) => (
            <Link key={n.to} to={n.to} className="block px-2 py-2 text-sm font-semibold text-slate-700 hover:text-sky-700">{n.label}</Link>
          ))}
        </nav>
      )}
    </header>
  );
}

function Footer() {  return (
    <footer className="bg-sky-900 border-t border-sky-800 mt-20 text-sky-100" data-testid="litlab-footer">
      <div className="max-w-6xl mx-auto px-6 py-14 grid grid-cols-1 md:grid-cols-4 gap-8">
        <div className="md:col-span-2">
          <div className="flex items-center gap-2.5">
            <LitLabMark className="w-11 h-11" variant="dark" />
            <div className="litlab-serif text-lg font-bold text-white">L.I.T. Labs, Inc.</div>
          </div>
          <p className="mt-4 text-sm text-sky-200 max-w-md">
            Long Island, New York based metallurgical testing lab serving aerospace,
            automotive, defense and other regulated industries since 1985.
          </p>
          <div className="mt-6 flex gap-3">
            <span className="inline-flex items-center gap-1.5 text-xs text-emerald-300 border border-emerald-400/40 bg-emerald-500/10 rounded px-2 py-1">
              <ShieldCheck className="w-3 h-3" /> Nadcap
            </span>
            <span className="inline-flex items-center gap-1.5 text-xs text-emerald-300 border border-emerald-400/40 bg-emerald-500/10 rounded px-2 py-1">
              <Award className="w-3 h-3" /> AC7006 · ISO/IEC 17025
            </span>
          </div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-sky-300 font-bold">Contact</div>
          <div className="mt-3 text-sm text-sky-100 space-y-1">
            <div>{CONTACT.address}</div>
            <div>Tel: {CONTACT.phone}</div>
            <div>Fax: 631-643-5628</div>
            <a href={`mailto:${CONTACT.email}`} className="text-amber-400 hover:text-amber-300">{CONTACT.email}</a>
          </div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-sky-300 font-bold">Explore</div>
          <div className="mt-3 space-y-1 text-sm">
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} className="block text-sky-200 hover:text-amber-300">{n.label}</Link>
            ))}
          </div>
        </div>
      </div>
      <div className="border-t border-sky-800">
        <div className="max-w-6xl mx-auto px-6 py-5 text-xs text-sky-300 flex flex-wrap justify-between gap-2">
          <div>© {new Date().getFullYear()} L.I.T. Labs, Inc. (formerly Long Island Testing Laboratories) · All rights reserved.</div>
          <div>Metallurgists · Analysts · Since 1985</div>
        </div>
      </div>
    </footer>
  );
}

/**
 * LitLabMark — SVG wordmark. Placeholder until the client provides the
 * official LitLab logo. Uses a crucible/beaker glyph paired with LIT text.
 * `variant` = "light" (default, dark ink on light bg) | "dark" (white on dark).
 */
export function LitLabMark({ className = "w-10 h-10", variant = "light" }) {
  const ink = variant === "dark" ? "#f8fafc" : "#0369a1";
  const accent = "#f59e0b";
  return (
    <svg viewBox="0 0 64 64" className={className} xmlns="http://www.w3.org/2000/svg" aria-label="L.I.T. Labs mark">
      <defs>
        <linearGradient id="litlab-mark-grad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={ink} />
          <stop offset="100%" stopColor={variant === "dark" ? "#38bdf8" : "#0ea5e9"} />
        </linearGradient>
      </defs>
      <rect x="2" y="2" width="60" height="60" rx="12" fill="url(#litlab-mark-grad)" />
      {/* Crucible outline */}
      <path d="M20 20 L44 20 L40 46 Q40 50 36 50 L28 50 Q24 50 24 46 Z"
             fill="none" stroke="#f8fafc" strokeWidth="2.5" strokeLinejoin="round" />
      {/* Liquid line */}
      <path d="M23 34 Q28 32 32 34 T41 34" fill="none" stroke={accent} strokeWidth="2" strokeLinecap="round" />
      {/* Bubbles */}
      <circle cx="28" cy="28" r="1.6" fill={accent} />
      <circle cx="34" cy="25" r="1.2" fill={accent} />
      <circle cx="37" cy="30" r="1" fill={accent} />
    </svg>
  );
}
